import React, { useCallback, useEffect } from 'react'

import { fetchApi } from '@hozana/api/fetchApi'
import { Translate } from '@hozana/intl/Translate'
import { useDispatch } from '@hozana/redux/hooks'
import { GTM } from '@hozana/tracking/gtm'

import { Button } from 'elements/button/Button'
import { Div } from 'elements/layout/Div'
import { P } from 'elements/text/P'

import { ICON } from 'config/icons'
import { closePopin } from 'general/actions'
import { Popin } from 'general/managers/popins/Popin'
import { POPINS } from 'general/managers/popins/constants'
import type { TPopinComponent } from 'general/managers/popins/types'

import { MESSAGE_TYPE } from 'modules/message/constants'
import { startConversationMutation } from 'modules/message/queries'
import { NOTIFICATION_TYPE } from 'modules/notification/constants'
import { markNotificationAsReadMutation } from 'modules/notification/queries'
import type { TNotification } from 'modules/notification/types'
import { UserAvatar } from 'modules/user/components/UserAvatar'
import { useUser } from 'modules/user/hooks/useUser'

export const getNewPrayerText = (type: NOTIFICATION_TYPE): Record<'single' | 'multiple', string> => {
  switch (type) {
    case NOTIFICATION_TYPE.NEW_PRAYER_ON_ARRIVAL:
      return {
        single: 'common:message.prayer.has-just-prayed-for-you.arrival',
        multiple: 'common:message.prayers.has-just-prayed-for-you.arrival',
      }
    case NOTIFICATION_TYPE.NEW_PRAYER_ON_BIRTHDAY:
      return {
        single: 'common:message.prayer.has-just-prayed-for-you.birthday',
        multiple: 'common:message.prayers.has-just-prayed-for-you.birthday',
      }
    default:
      return {
        single: 'common:message.prayer.has-just-prayed-for-you.intention',
        multiple: 'common:message.prayers.has-just-prayed-for-you.intention',
      }
  }
}

export type TPrayerPromptPopinOptions = TNotification<NOTIFICATION_TYPE.NEW_PRAYER_ON_INTENTION>

export const PrayerPromptPopin: TPopinComponent<TPrayerPromptPopinOptions> = ({
  onClose,
  options: {
    id: notificationId,
    objectId: intentionId,
    type,
    payload: {
      prayerId,
      user: { id: correspondentId, name: correspondentName },
    },
  },
}) => {
  const dispatch = useDispatch()
  const me = useUser()

  useEffect(() => {
    if (notificationId) {
      dispatch(fetchApi(markNotificationAsReadMutation({ notificationId })))
    }
  }, [dispatch, notificationId])

  const thanksPrayer = useCallback(() => {
    GTM.trackEvent(GTM.EVENTS.THANKS_PRAYER_CLICKED)
    dispatch(
      fetchApi(
        startConversationMutation({
          users: [correspondentId],
          type: MESSAGE_TYPE.THANKS_INTENTION_PRAYER,
          payload: {
            userId: me.id,
            correspondentId,
            intentionId,
          },
          prayerId,
        }),
      ),
    ).then(() => {
      dispatch(closePopin(POPINS.PrayerPromptPopin))
      window.location.href = `/messages?userId=${correspondentId}`
    })
  }, [correspondentId, dispatch, intentionId, me.id, prayerId])

  return correspondentId ? (
    <Popin
      w="400px"
      popoverOnMobile
      onClose={onClose}
      ornament={{ icon: ICON.INTENTION, color: 'yellow', isAboveCircleDiv: true, bgCircleDiv: 'white' }}
    >
      <Div align="middleCenter" w="80%" m="48px auto 0 auto">
        <UserAvatar userId={correspondentId} size="small" m="0 10px" />
        <P color="black" bold={true} w="50%" m="0 0 0 1em">
          {/* '_' is translated to 'someone' */}
          <Translate values={{ userName: correspondentName || '_' }}>{getNewPrayerText(type).single}</Translate>
        </P>
      </Div>

      <Div align="middleCenter" m="23px 0px 26px 0px" w="100%">
        <Button size="normal" bold colors="yellow" w="80%" onClick={() => thanksPrayer()}>
          trans:common:intention.prayed.thank-again
        </Button>
      </Div>
    </Popin>
  ) : null
}
